import { FC } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useParams } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';

import { ReactComponent as PlusIcon } from '@/assets/icons/plus.svg';
import Hidden from '@/components/Hidden';
import Text from '@/components/Text';
import { getInitialsFromString } from '@/utils/helpers';
import { RootState } from '@/utils/store';
import { hexToRgba } from '@/utils/theme';

const StyledTeams = styled.nav`
  width: 56px;
  padding: 24px 8px;
  display: grid;
  grid-template-columns: 32px;
  grid-row-gap: 16px;
  justify-content: center;
  align-self: flex-start;
  ${({ theme }) => theme.breakpoints.mediumDesktop} {
    z-index: 101;
    background: ${({ theme }) => theme.colors.blue};
    height: calc(100vh - 73px);
    align-content: start;
  }
`;

export const TeamInitials = styled(Text)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 32px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
`;

export const Team = styled(NavLink)`
  &.active {
    position: relative;
    &::before {
      content: '';
      position: absolute;
      border-radius: 6px;
      width: 40px;
      height: 40px;
      top: -4px;
      left: -4px;
      border: 2px solid ${({ theme }) => theme.colors.gray};
    }
  }
  &.god-mode {
    span {
      background: linear-gradient(136.91deg, #ff8846 3.13%, #ff711f 96.98%);
    }
  }
  &.home-mode {
    span {
      background: linear-gradient(136.85deg, #547a89 2.42%, #2f5766 99.4%);
    }
  }
`;

const NewTeam = styled(TeamInitials)`
  color: ${({ theme }) => theme.colors.white};
  background: ${({ theme }) => hexToRgba(theme.colors.white, 0.12)};
`;

const StyledTooltip = styled(ReactTooltip)`
  font-weight: 700;
  font-size: ${({ theme }) => theme.typography.smallBold.fontSize};
  opacity: 1 !important;
  padding: 4px 8px !important;
  font-family: ${({ theme }) => theme.typography.smallBold.fontFamily};
`;

export const Teams: FC = () => {
  const teams = useSelector((state: RootState) => state.teams);

  const { teamId } = useParams();

  const teamIds = Object.keys(teams)
    .filter((id) => !isNaN(Number(id)))
    // TODO: remove this filter
    .filter((teamId) => !['3', '4', '5', '13'].includes(teamId));

  return (
    <StyledTeams>
      <Team
        to="/teams/all/top-videos"
        className={`${teamId === 'all' ? 'active' : ''} god-mode`}
        data-tip="God Mode"
        data-for="sidebar-teams-tooltip"
      >
        <TeamInitials as="span">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M7.5 18.7494L8.75 12.4994L3.75 10.6244L12.5 1.24937L11.25 7.49937L16.25 9.37437L7.5 18.7494Z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </TeamInitials>
      </Team>
      {teamIds.map((id) => (
        <Team
          key={id}
          to={`/teams/${id}`}
          className={id === teamId ? 'active' : ''}
          data-tip={teams[id].name}
          data-for="sidebar-teams-tooltip"
        >
          {teams[id].icon ? (
            <TeamInitials
              as="img"
              src={
                teams[id].icon!.versions.icon ||
                teams[id].icon!.versions.original
              }
              alt={teams[id].name}
            />
          ) : (
            <TeamInitials as="span" $type="smallBold">
              {getInitialsFromString(teams[id].name)}
            </TeamInitials>
          )}
        </Team>
      ))}
      <Hidden>
        <Team to="/" data-tip="Add a new Team" data-for="sidebar-teams-tooltip">
          <NewTeam>
            <PlusIcon />
          </NewTeam>
        </Team>
      </Hidden>
      <StyledTooltip
        id="sidebar-teams-tooltip"
        place="right"
        effect="solid"
        backgroundColor="#ffffff"
        textColor="#222222"
      />
    </StyledTeams>
  );
};
